import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { ButtonView, View, ViewBaseProps } from '@shared/components/View';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import { Entity } from '@shared/data/entity/Entity';
import { CardActionMenu } from '@shared/scenes/cards/CardActionMenu';
import { API } from '@shared/types/api';
import { usePaywallExperiment } from '@shared/util/featureFlags';
import { useDraggableContext } from '../../listView/components/DraggableContext';
import { OpenEntityHandler } from '../hooks';
import { EntitySelectionCheckbox } from './cardOptions/EntitySelectionCheckbox';
import { EntitySelectionOverlay } from './cardOptions/EntitySelectionOverlay';
import DefaultCardStyles from './DefaultCardStyles.module.css';

interface Props extends ViewBaseProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  entity: Entity;
  openEntity?: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  hasOverflowMenu?: boolean;
  isHovered?: boolean;
  cardStyles?: CollectionViewCardStyles;
}

export interface CollectionViewCardStyles {
  card?: string;
  content?: string;
  cardActions?: string;
  overflowButton?: string;
  overflowButtonHovered?: string;
  selectionCheckbox?: string;
}

export function CollectionViewCard({
  children,
  workflowTemplate,
  entity,
  openEntity,
  getIsCardBeforePaidCutoff,
  hasOverflowMenu = true,
  isHovered = false,
  cardStyles,
  ...viewProps
}: Props) {
  const { isRedactedGroup } = usePaywallExperiment();
  const isCardBeforePaidCutoff = getIsCardBeforePaidCutoff(entity);
  const isRedacted = isRedactedGroup && isCardBeforePaidCutoff;
  const { isDragging } = useDraggableContext();

  const { selectionActive, isEntitySelected } = useEntitySelection();
  const showSelection = selectionActive && !isRedacted && !isDragging;
  const isSelected = isEntitySelected(entity.id);
  const showOverflowMenu = hasOverflowMenu && !showSelection && !isRedacted && !isDragging;

  const onClick = () => {
    openEntity?.({ entityId: entity.id, entityViewTemplateId: entity.navigationViewTemplateId });
  };

  return (
    <View
      className={[
        DefaultCardStyles.card,
        cardStyles?.card,
        isRedacted && DefaultCardStyles.redacted,
        isSelected && showSelection && DefaultCardStyles.selected,
      ]}
      {...viewProps}
    >
      {!showSelection && <ButtonView onClick={onClick} />}
      <View className={[DefaultCardStyles.cardActions, cardStyles?.cardActions]}>
        {showOverflowMenu && (
          <CardActionMenu
            entity={entity}
            workflowTemplate={workflowTemplate}
            className={[cardStyles?.overflowButton, isHovered && cardStyles?.overflowButtonHovered]}
          />
        )}
        {showSelection && (
          <EntitySelectionCheckbox
            isSelected={isSelected}
            className={cardStyles?.selectionCheckbox}
          />
        )}
      </View>
      {showSelection && <EntitySelectionOverlay entity={entity} />}
      <View
        className={[DefaultCardStyles.cardContent, cardStyles?.content]}
        onClick={onClick}
        tabIndex={-1}
      >
        {children}
      </View>
    </View>
  );
}
